import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { ContestWizardState } from '../types';
import CampaignFeatureImagePicker from '../../campaignDetail/CampaignFeatureImagePicker';
import { RichMediaShareSizeHint } from '../../../../components/Common/RichMediaShareSizeHint';
import { RichTextEditor } from '../../../../components/Common/RichTextEditor';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../midnightStyles';

interface Props {
    state: ContestWizardState;
    onChange: (patch: Partial<ContestWizardState>) => void;
    onOpenAiAssist: () => void;
}

export function CampaignIdentityStep({ state, onChange, onOpenAiAssist }: Props) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box>
                    <Typography
                        sx={{ ...editorialHeadlineSx, fontSize: { xs: 28, md: 36 }, color: midnight.onSurface }}>
                        Campaign Identity
                    </Typography>
                    <Typography sx={{ color: midnight.onSurfaceVariant, mt: 1 }}>
                        Set the visual and title that represent your campaign everywhere.
                    </Typography>
                </Box>
                <Button
                    onClick={onOpenAiAssist}
                    startIcon={<AutoAwesomeIcon />}
                    sx={{
                        flexShrink: 0,
                        color: midnight.secondary,
                        border: `1px solid rgba(0,253,193,0.25)`,
                        borderRadius: 999,
                        px: 2,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        '&:hover': { bgcolor: 'rgba(0,253,193,0.08)' },
                    }}>
                    AI Assist
                </Button>
            </Box>

            <Box>
                <Typography sx={editorialLabelSx}>
                    Feature Image
                    <RichMediaShareSizeHint inline sx={{ color: midnight.onSurfaceVariant }} />
                </Typography>
                <CampaignFeatureImagePicker
                    preview={state.feature_image_preview}
                    onSelect={(file, preview) => onChange({ feature_image_file: file, feature_image_preview: preview })}
                    onClear={() => onChange({ feature_image_file: null, feature_image_preview: null })}
                />
            </Box>

            <Box>
                <Typography sx={editorialLabelSx}>Campaign Title</Typography>
                <TextField
                    fullWidth
                    placeholder="e.g. Midnight Summer Drop 2024"
                    value={state.title}
                    onChange={e => onChange({ title: e.target.value })}
                    sx={editorialInputSx}
                />
            </Box>

            <Box>
                <Typography sx={editorialLabelSx}>Campaign Brief</Typography>
                <RichTextEditor
                    shellVariant="brandDashboard"
                    placeholder="Describe goals, tone, and what success looks like."
                    value={state.description || ''}
                    onChange={description => onChange({ description })}
                />
            </Box>
        </Box>
    );
}
